// src/services/patientApi.js
// Fetches, creates, updates and deletes patient records for the authenticated user.
// API: GET /api/patients/my  → PatientResponse[]
//      POST /api/patients    → PatientResponse
//      PUT  /api/patients/:id → PatientResponse
//      DELETE /api/patients/:id → 204

import axiosInstance from './api';

/**
 * Fetch all saved patient profiles for the current user.
 * The backend always returns the "self" record first.
 * @returns {Promise<PatientResponse[]>}
 */
export const getMyPatients = async () => {
  const { data } = await axiosInstance.get('/patients/my');
  return Array.isArray(data) ? data : (data.content ?? []);
};

/**
 * Save a new patient record.
 * Pass isSelf:true to upsert the user's own "self" profile.
 * @param {PatientRequest} req
 * @returns {Promise<PatientResponse>}
 */
export const createPatient = async (req) => {
  const { data } = await axiosInstance.post('/patients', req);
  return data;
};

/**
 * Helper: turn a PatientResponse into the shape BookAppointment's `patient` state expects.
 */
export const patientToBookingForm = (p) => ({
  name:     p.fullName  || '',
  phone:    p.phone     || '',
  email:    p.email     || '',
  age:      p.age != null ? String(p.age) : '',
  gender:   p.gender    || '',
  relation: p.relation  || (p.isSelf ? 'SELF' : 'OTHER'),
  reason:   '',           // never pre-filled — specific to each appointment
});

/**
 * Helper: turn a PatientResponse into the shape Checkout's `address` state expects.
 * Note: address-specific fields (line1, line2, city, state, pincode) are not stored
 * in the patient record, so they are left blank for the user to fill in.
 */
export const patientToCheckoutForm = (p) => ({
  fullName: p.fullName || '',
  phone:    p.phone    || '',
  line1:    '',
  line2:    '',
  city:     '',
  state:    '',
  pincode:  '',
  type:     'Home',
});